import { configureCompat, createApp } from 'vue';
import { createHead } from '@unhead/vue/client';
import VueClickAway from 'vue3-click-away';
import { captureFaroError, initializeFaroTelemetry } from '@/plugins/faro-loader.ts';
import { iconsPlugin } from '@/plugins/svg-icons.ts';
import pinia from '@/pinia/index.js';
import { initStores } from '@/store/index.js';
import Caching from '@/plugins/caching.ts';
import { SignedUrlPlugin } from '@/plugins/signedUrl/index.ts';
import NewRelicBrowser, { VueNewRelic } from '@/plugins/newrelic-browser.ts';
import { initializeStartupTiming, markStartupPhase } from '@/telemetry/startupTiming.ts';
import ShadowRootPlugin from './plugins/shadow-root.js';
import VueOpenReplay from './plugins/vue-openreplay.js';
import './plugins/newrelic-loader.js';
import App from './App.vue';
import router from './router/index.ts';
import globalMixin from './mixins/Global.ts';
import './custom.js';
import './plugins.js';
import './plugins/outdated-browser.js';
import './plugins/modernizr.js';
import { VueLsPlugin } from './plugins/vue-ls.ts';
import i18n from './plugins/vue-i18n.js';
import ElementPlusPlugin from './plugins/element-plus.ts';
import './plugins/event-bus.ts';
import VueWait from './plugins/vue-wait.ts';
import VueAuth, { authService } from './plugins/vue-auth.ts';
import VueMfaAuthPlugin from './plugins/vue-mfa-auth.ts';
import BroadcastingPlugin from './plugins/broadcasting.ts';
import VueAxios from './plugins/vue-axios.ts';
import VueNotification from './plugins/vue-notification.ts';
import { VueSentry } from './plugins/sentry.ts';
import { vueMixpanelPlugin } from './plugins/mixpanel.ts';
import gtmPlugin from './plugins/gtm.ts';
import { virtualScrollerPlugin } from './plugins/vue-virtual-scroller.ts';
import activixUiPlugin from './plugins/activix-ui.ts';
import VueApi from './plugins/vue-api.ts';
import './plugins/activix-player.js';
import tippyPlugin from './plugins/tippy.ts';
import Directives from './plugins/directives.ts';
import VueOptimizely from './plugins/vue-optimizely.ts';
import './plugins/vh-fix.js';
import './plugins/chunk-reload.ts';
import VueBeamer from './plugins/vue-beamer.ts';
import VueForceNextTick from './plugins/vue-force-next-tick.ts';
import vueZiggeo from './plugins/ziggeo.ts';
import { canPlugin } from './plugins/can.js';
import VueModal from './plugins/vue-modal.ts';
import StatusPage from './plugins/status-page.ts';
import Browser from './plugins/browser.js';
import ExtensionDetection from './plugins/chrome-extensions.ts';
import BreakpointsPlugin from './plugins/breakpoints.ts';

// Styles
import './assets/less/app.less';
import './assets/scss/element-overrides.scss';

NewRelicBrowser.withAgent((agent) => {
    agent.setCustomAttribute('app_version', process.env.VUE_APP_VERSION || 'unknown');
    agent.setCustomAttribute('app_build_id', process.env.VUE_APP_BUILD_ID || 'unknown');
});
initializeStartupTiming();
initializeFaroTelemetry();

const app = createApp(App);
const head = createHead();

app.config.performance = true;

configureCompat({
    ATTR_ENUMERATED_COERCION: 'suppress-warning',
    ATTR_FALSE_VALUE: false,
    INSTANCE_LISTENERS: false,
});

app.config.errorHandler = (error) => {
    captureFaroError(error);
    console.error(error); // eslint-disable-line no-console
};

// NOTE: Injected deps for components are provided within plugin modules,
// e.g. $browser is provided by Browser plugin, $feature is provided by VueOptimizely plugin, etc.
app.mixin(globalMixin);
app.use(BreakpointsPlugin);
app.use(VueAxios);
app.use(VueWait);
app.use(VueClickAway);
app.use(Directives);
app.use(tippyPlugin);
app.use(VueModal);
app.use(StatusPage);
app.use(Browser);
app.use(ExtensionDetection);
app.use(ShadowRootPlugin);
app.use(Caching);
app.use(VueSentry);
app.use(VueAuth);
app.use(BroadcastingPlugin);
app.use(VueApi);
app.use(router);
app.use(pinia);
app.use(i18n);
app.use(VueLsPlugin);
app.use(VueMfaAuthPlugin);
app.use(ElementPlusPlugin);
app.use(VueNotification); // Must be after elementPlusPlugin: Element Plus overwrites $notify via ElNotification
app.use(head);
app.use(iconsPlugin);
app.use(activixUiPlugin);
app.use(virtualScrollerPlugin);
app.use(canPlugin);
app.use(vueMixpanelPlugin);
app.use(vueZiggeo);
app.use(VueOpenReplay);
app.use(VueOptimizely);
app.use(VueBeamer);
app.use(VueForceNextTick);
app.use(VueNewRelic);
app.use(SignedUrlPlugin);

if (gtmPlugin) {
    app.use(gtmPlugin);
}

// Initialize stores after plugins are loaded
initStores();
markStartupPhase('plugins_registered');

// Validate session and trigger login redirect.
// Must run after app plugins are registered (especially pinia/auth dependencies).
authService.handshake();

// Handle auth callback routes before mounting the app
// This ensures authentication completes and redirects before App.vue is created
const isAuthCallback =
    window.location.pathname === '/auth/callback' ||
    window.location.pathname === '/auth/mfa-callback';

if (isAuthCallback) {
    // On callback routes, wait for authentication to complete and redirect
    // The auth service starts the redirect, then we mount so app initialization can satisfy route guards
    if (window.location.pathname === '/auth/callback') {
        authService.handleAuthentication().then(() => {
            app.mount('#app');
            markStartupPhase('app_mounted');
        });
    } else {
        // MFA callback - import mfaAuthService dynamically
        import('./plugins/vue-mfa-auth.ts').then(({ mfaAuthService }) => {
            mfaAuthService.handleAuthentication().then(() => {
                app.mount('#app');
                markStartupPhase('app_mounted');
            });
        });
    }
} else {
    // Normal routes - mount immediately (don't wait for router.isReady to avoid deadlock)
    // Router guards may need App.vue to be mounted to fetch user/context
    app.mount('#app');
    markStartupPhase('app_mounted');
}
